import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { InquiryMgmtStateType, ApiGetDataStatus } from '@redux/types'
import {
	InquiryType,
	InquiryCommentType,
	InquiryStatusLogType,
	InquiryStatusType,
} from '@schemas'

const initialState: InquiryMgmtStateType = {
	// triggers
	triggerInitInquiryMgmt: false,
	triggerLoadMoreInquiries: false,
	triggerLoadInquiryDetail: false,
	triggerUpdateStatus: false,
	triggerUpdateHandler: false,
	triggerAddComment: false,
	triggerDeleteComment: false,
	triggerDeleteInquiry: false,

	// list
	statusFilter: 'all',
	searchQuery: '',
	inquiryList: [],
	inquiryListStatus: 'init',
	hasMore: false,
	isLoadingMore: false,

	// timeline drawer
	selectedInquiry: null,
	selectedInquiryStatus: 'init',
	isTimelineOpen: false,
	comments: [],
	statusLogs: [],

	// user list
	userList: [],

	// payloads
	updateStatusPayload: { inquiryId: '', status: '', note: '' },
	updateHandlerPayload: { inquiryId: '', handlerId: '' },
	addCommentPayload: { inquiryId: '', body: '', isInternal: true },
	deleteCommentPayload: { inquiryId: '', commentId: '' },
	deleteInquiryId: '',
}

const slice = createSlice({
	name: 'inquiryMgmt',
	initialState,
	reducers: {
		// triggers
		TRIGGER_INIT_INQUIRY_MGMT: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerInitInquiryMgmt = action.payload
		},
		TRIGGER_LOAD_MORE_INQUIRIES: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerLoadMoreInquiries = action.payload
		},
		TRIGGER_LOAD_INQUIRY_DETAIL: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerLoadInquiryDetail = action.payload
		},
		TRIGGER_UPDATE_STATUS: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerUpdateStatus = action.payload
		},
		TRIGGER_UPDATE_HANDLER: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerUpdateHandler = action.payload
		},
		TRIGGER_ADD_COMMENT: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerAddComment = action.payload
		},
		TRIGGER_DELETE_COMMENT: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerDeleteComment = action.payload
		},
		TRIGGER_DELETE_INQUIRY: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerDeleteInquiry = action.payload
		},

		// list
		STATUS_FILTER: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryStatusType | 'all'>,
		) => {
			state.statusFilter = action.payload
		},
		SEARCH_QUERY: (state: InquiryMgmtStateType, action: PayloadAction<string>) => {
			state.searchQuery = action.payload
		},
		INQUIRY_LIST: (state: InquiryMgmtStateType, action: PayloadAction<InquiryType[]>) => {
			state.inquiryList = action.payload
		},
		APPEND_INQUIRY_LIST: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryType[]>,
		) => {
			// 같은 행이 두 번 붙지 않게 (페이지 경계에서 새 문의가 끼어든 경우)
			const known = new Set(state.inquiryList.map(anItem => anItem.id))
			state.inquiryList.push(...action.payload.filter(anItem => !known.has(anItem.id)))
		},
		INQUIRY_LIST_STATUS: (
			state: InquiryMgmtStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.inquiryListStatus = action.payload
		},
		HAS_MORE: (state: InquiryMgmtStateType, action: PayloadAction<boolean>) => {
			state.hasMore = action.payload
		},
		IS_LOADING_MORE: (state: InquiryMgmtStateType, action: PayloadAction<boolean>) => {
			state.isLoadingMore = action.payload
		},
		/** 목록 행과(같은 id 면) 드로어 대상까지 부분 갱신. 목록 전체를 다시 받지 않는다. */
		PATCH_INQUIRY: (
			state: InquiryMgmtStateType,
			action: PayloadAction<{ id: string; changes: Partial<InquiryType> }>,
		) => {
			const { id, changes } = action.payload
			const index = state.inquiryList.findIndex(anItem => anItem.id === id)
			if (index >= 0) {
				state.inquiryList[index] = { ...state.inquiryList[index], ...changes }
			}
			if (state.selectedInquiry && state.selectedInquiry.id === id) {
				state.selectedInquiry = { ...state.selectedInquiry, ...changes }
			}
		},
		REMOVE_INQUIRY: (state: InquiryMgmtStateType, action: PayloadAction<string>) => {
			state.inquiryList = state.inquiryList.filter(anItem => anItem.id !== action.payload)
		},

		// timeline drawer
		SELECTED_INQUIRY: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryType | null>,
		) => {
			state.selectedInquiry = action.payload
		},
		SELECTED_INQUIRY_STATUS: (
			state: InquiryMgmtStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.selectedInquiryStatus = action.payload
		},
		IS_TIMELINE_OPEN: (state: InquiryMgmtStateType, action: PayloadAction<boolean>) => {
			state.isTimelineOpen = action.payload
		},
		COMMENTS: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryCommentType[]>,
		) => {
			state.comments = action.payload
		},
		STATUS_LOGS: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryStatusLogType[]>,
		) => {
			state.statusLogs = action.payload
		},

		// user list
		USER_LIST: (
			state: InquiryMgmtStateType,
			action: PayloadAction<{ id: string; name: string }[]>,
		) => {
			state.userList = action.payload
		},

		// payloads
		UPDATE_STATUS_PAYLOAD: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryMgmtStateType['updateStatusPayload']>,
		) => {
			state.updateStatusPayload = action.payload
		},
		UPDATE_HANDLER_PAYLOAD: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryMgmtStateType['updateHandlerPayload']>,
		) => {
			state.updateHandlerPayload = action.payload
		},
		ADD_COMMENT_PAYLOAD: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryMgmtStateType['addCommentPayload']>,
		) => {
			state.addCommentPayload = action.payload
		},
		DELETE_COMMENT_PAYLOAD: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryMgmtStateType['deleteCommentPayload']>,
		) => {
			state.deleteCommentPayload = action.payload
		},
		DELETE_INQUIRY_ID: (state: InquiryMgmtStateType, action: PayloadAction<string>) => {
			state.deleteInquiryId = action.payload
		},
	},
})

export const inquiryMgmtActions = slice.actions
export default slice.reducer
